import { format } from 'date-fns'
import {
  MIXED_FORM_CONTRACT_TYPE,
  IContractOptions,
  IFieldOption,
  IReclamationForm,
  IDestructionCertificateProduct,
  IDestructionCertificateForm,
  IContractFormField,
  IDirectFrontlineContractFormField,
} from '../interfaces/IContracts'
import { getJwtUserDetails } from './api'
import {
  FORM_DATE_REQUEST_FORMAT,
  ENGAGEMENTS_PER_PAGE,
  CLIENT_EMAIL,
  CONTENT_DATA,
  DCR_EMAIL,
  KAM_EMAIL,
} from '../enums/common'
import { AUTRE, OUI } from '../constants/form'
import { ROUTES } from '../enums/routes'
import { timezoneOffset, toNumber } from './helpers'
import { IUsersOptions } from '../interfaces/IUsers'
import {
  ICustomObject,
  SEND_EMAIL_API_TYPE,
  TSendEmailData,
} from '../interfaces/ICommonComponents'
import {
  RECLAMATION_AREA,
  MIXED_FORM_CONTRACT_TYPE_ROUTES_CLIENT,
  MIXED_FORM_CONTRACT_TYPE_ROUTES_GENERAL,
} from '../constants/mixedForms'

export const prepareReclamationRequest = (values: any, clientId: string) => {
  const toBoolean = (field: string) => field === OUI

  return {
    clientId: parseInt(clientId, 10),
    userId: getJwtUserDetails().id,
    type: values.type,
    laboratory: values.laboratory,
    incidentDate: values.incidentDate
      ? format(values.incidentDate, FORM_DATE_REQUEST_FORMAT)
      : '',
    timezoneOffset: timezoneOffset(),
    notifier: values.notifier !== AUTRE ? values.notifier : values.other,
    agreementContactInfo: toBoolean(values.agreementContactInfo),
    contactInfo: {
      fullName: values.fullName,
      officeName: values.officeName,
      address: values.reclamationAddress,
      postalCode: values.postalCode,
      city: values.city,
      phone: values.phone,
      fax: values.fax,
      email: values.email,
    },
    agreementExtraDetails: toBoolean(values.agreementExtraDetails),
    patient: {
      initials: values.initials,
      age: parseInt(values.age, 10),
      gender: values.gender?.length ? values.gender[0] : null,
    },
    product: {
      product: values.productConcerned,
      cipCode: values.productCipCode,
      lot: values.lot,
      expirationDate: values.expirationDate
        ? format(values.expirationDate, FORM_DATE_REQUEST_FORMAT)
        : null,
    },
    description: values.description,
    specialtyAvailable: toBoolean(values.specialtyAvailable),
    signature: values.signature,
  } as IReclamationForm
}

export const checkValidation = (validation: any, setShowError: any) => {
  if (validation() !== RECLAMATION_AREA) {
    setShowError(false)
  } else {
    setShowError(true)
  }
}

export const isSmallerThan = (value: Date, comparedValue = new Date()) => {
  return value.getTime() < comparedValue.getTime() || 'smallerThanNow'
}

export const getContractRoute = (
  isMixedForm?: boolean,
  clientId?: number,
  year?: number | null,
  mixedFormType?: MIXED_FORM_CONTRACT_TYPE
) => {
  if (isMixedForm && mixedFormType) {
    return clientId
      ? MIXED_FORM_CONTRACT_TYPE_ROUTES_CLIENT[mixedFormType]
      : MIXED_FORM_CONTRACT_TYPE_ROUTES_GENERAL[mixedFormType]
  }

  if (year) {
    return clientId
      ? ROUTES.clientEngagementsHistory
      : ROUTES.engagementsHistory
  }

  return clientId ? ROUTES.clientEngagements : ROUTES.engagements
}

export const prepareOptions = (options: IContractOptions[]) => {
  return options.map(({ id, label }) => ({
    name: label,
    value: id.toString(),
  }))
}

/**
 * Convert form values into fields with selectedOptionId
 */
const getFieldsValues = (values: any, data: any) => {
  const fields: IFieldOption[] = []

  data?.fields?.forEach((item: any) => {
    const isParent = FIELD_PARENT_TOKENS.some((tok) => item.label.includes(tok))

    if (isParent) {
      const checkedOptionIds = item.options
        .filter((opt: { id: any }) => values[`field${opt.id}`])
        .map((opt: { id: any }) => opt.id)
      fields.push({
        id: item.id,
        selectedOptionId:
          checkedOptionIds.length === 1 && checkedOptionIds[0] === null
            ? []
            : checkedOptionIds,
      })
    } else {
      const selected = [toNumber(values[`field${item.id}`])]
      fields.push({
        id: item.id,
        selectedOptionId: isNaN(selected[0]) ? [] : selected,
      })
    }
  })

  return fields
}

export const FIELD_PARENT_TOKENS = [
  'Offre FRONTLINE',
  'Offre FRONTCONTROL WORMER',
  'Offre FRONTPRO',
  'Remise référencement 3 gammes (FRONTLINE + FRONTCONTROL WORMER + FRONTPRO) : 4%',
]

const FRONTPRO_SELECT_SUBFIELD_TOKENS = [
  "Commande d'implantation, remise gamme FRONTPRO",
]

const SKIP_SUBFIELDS_TOKENS = [
  'Offre FRONT',
  'Remise référencement',
  "Commande d'implantation",
]

/**
 * Group contract fields into nested structure
 */
export const groupContractFields = (
  fields: IContractFormField[] = []
): IDirectFrontlineContractFormField[] => {
  const out: IDirectFrontlineContractFormField[] = []
  const skipped = new Set<number>()

  fields.forEach((field, idx) => {
    if (skipped.has(idx)) return

    const isParent = FIELD_PARENT_TOKENS.some((tok) =>
      field.label.includes(tok)
    )

    if (isParent) {
      const parent: IDirectFrontlineContractFormField = {
        id: field.id,
        label: field.label,
        order: field.order,
        mandatory: field.mandatory,
        isCheckbox: true,
        subfields: [],
        options: [...field.options],
      }

      const optSubfields = (field.options ?? [])
        .filter(
          (opt) => !SKIP_SUBFIELDS_TOKENS.some((tok) => opt.label.includes(tok))
        )
        .map((opt) => ({
          id: opt.id,
          label: opt.label,
          order: opt.order,
          mandatory: false,
          isCheckbox: true,
          subfields: [],
          options: [],
        }))

      parent.subfields!.push(...optSubfields)

      // FRONTPRO special case
      if (field.label.includes('FRONTPRO')) {
        const frontproSubfield = fields[idx + 1]
        if (
          frontproSubfield &&
          FRONTPRO_SELECT_SUBFIELD_TOKENS.some((tok) =>
            frontproSubfield.label.includes(tok)
          )
        ) {
          let isSubfieldMandatory = frontproSubfield.mandatory
          let subfieldOptions = frontproSubfield.options ?? []

          if (
            frontproSubfield.label.includes(FRONTPRO_SELECT_SUBFIELD_TOKENS[0])
          ) {
            isSubfieldMandatory = true
            subfieldOptions = subfieldOptions.filter(
              (option) => !option.label.includes('Non')
            )
          }
          const newFrontproSubfield: IDirectFrontlineContractFormField = {
            id: frontproSubfield.id,
            label: frontproSubfield.label,
            order: frontproSubfield.order,
            mandatory: isSubfieldMandatory,
            isCheckbox: false,
            subfields: [],
            options: subfieldOptions,
          }
          parent.subfields!.push(newFrontproSubfield)
          skipped.add(idx + 1)
        }
      }

      out.push(parent)
      return
    }

    out.push({
      id: field.id,
      label: field.label,
      order: field.order,
      mandatory: field.mandatory,
      isCheckbox: false,
      subfields: [],
      options: field.options ? [...field.options] : [],
    })
  })

  return out
}

const getContactNameById = (contactPersons: IUsersOptions[], id: string) => {
  return contactPersons.find((element: IUsersOptions) => {
    return element.value === id
  }) as IUsersOptions
}

export const prepareContractRequestBody = (
  values: any,
  data: any,
  clientId: string,
  contactPersons: IUsersOptions[],
  newContactPerson: IUsersOptions
) => {
  const contactPerson: IUsersOptions = getContactNameById(
    [...contactPersons, newContactPerson],
    values.contactPerson
  )

  return {
    formId: data.formId,
    signature: values.signature,
    client: {
      id: toNumber(clientId),
      contactPerson: contactPerson.name,
    },
    fields: getFieldsValues(values, data),
  }
}

export const prepareSendEmailRequestBody = (
  emailCheckbox: boolean,
  data: any,
  type: SEND_EMAIL_API_TYPE
) => {
  return {
    kamEmail: data?.client?.kamEmail,
    dcrEmail: data?.client?.dcrEmail,
    clientEmail: emailCheckbox ? data?.client?.email : null,
    type,
  }
}

export const prepareManualSendEmailRequestBody = (
  data: TSendEmailData,
  clientEmail: string,
  key: string = CLIENT_EMAIL
) => {
  const kamEmail = data?.kamEmail ? { [KAM_EMAIL]: data?.kamEmail } : {}
  const dcrEmail = data?.dcrEmail ? { [DCR_EMAIL]: data?.dcrEmail } : {}
  const contentData = data?.contentData
    ? { [CONTENT_DATA]: data?.contentData }
    : {}

  return {
    ...kamEmail,
    ...dcrEmail,
    [key]: clientEmail,
    type: data?.type,
    ...contentData,
    entityId: data?.entityId,
  }
}

export const getMissingData = (obj: ICustomObject) =>
  Object.keys(obj).filter((key) => obj[key] === (null || ''))

export const createContractsQuery = (
  year: number,
  offset: number,
  size: number
) => {
  const query = `year=${year}&size=${size}&offset=${
    offset * ENGAGEMENTS_PER_PAGE
  }`

  return query
}

export const prepareProductsPhoto = (photo: string) => {
  if (!photo) return ''

  const uploadedImageData = JSON.parse(photo)

  return uploadedImageData.dataURL
}

export const prepareDestructionCertificateRequestBody = (
  values: any,
  data: any,
  clientId: number,
  contactPersons: IUsersOptions[],
  newContactPerson: IUsersOptions,
  products: IDestructionCertificateProduct[],
  comment: string
) => {
  const contactPerson: IUsersOptions = getContactNameById(
    [...contactPersons, newContactPerson],
    values.contactPerson
  )

  return {
    clientId,
    noOfExpiredProducts: values.noOfExpiredProducts,
    noOfDamagedProducts: values.noOfDamagedProducts,
    signatoryFirstName: contactPerson.firstName,
    signatoryLastName: contactPerson.lastName,
    signature: values.signature,
    city: values.city,
    discountMarket: values.discountMarket,
    photo: prepareProductsPhoto(values.photo),
    timezoneOffset: timezoneOffset(),
    signedAt: values.signedAt
      ? format(new Date(values.signedAt), FORM_DATE_REQUEST_FORMAT)
      : '',
    comment,
    products,
  } as IDestructionCertificateForm
}

export const handleFormModal = (
  formValues: any,
  callback: (modalStatus: boolean) => void
) => {
  let modalStatus = false

  Object.entries(formValues).forEach((item: any) => {
    if ((item[1] !== '' && typeof item[1] === 'string') || item[1] === false) {
      modalStatus = true
    }
  })

  callback(modalStatus)
}
